import { Fragment, type Node as PmNode, Slice } from "prosemirror-model";
import { type EditorState, Selection, TextSelection } from "prosemirror-state";
import type { EditorProps, EditorView } from "prosemirror-view";
import { isEntityPaste } from "../froala/content-manager";
import {
  extractInternalContent,
  isInternalStorydocPaste,
} from "../froala/paste-cleaner";
import { enclosingList, linkSelectionTo, normalizeUrl } from "./commands";
import {
  MAX_LIST_DEPTH,
  applyDefaultAlignment,
  blockImpliedStyle,
  blocksToListItems,
  capListDepth,
  dropEmptyParagraphs,
  listDepthAt,
  mergeAdjacentLists,
  normalizePastedTypography,
  stripTrailingBreaks,
} from "./normalize";
import { normalizeExternalHtml } from "./paste-html";
import { slackTextyToHtml } from "./paste-slack";
import { parser, serializeHtml } from "./html";
import { textSchema } from "./schema";

/**
 * Paste pipeline (spec §2.2): clipboard → sanitize → normalize → insert.
 *
 * It is built entirely on ProseMirror's own clipboard hooks, in the order the
 * view calls them:
 *   1. transformPastedText / transformPastedHTML — string level: detect the
 *      source, unwrap Storydoc layout wrappers. Nothing else: the schema's
 *      parse rules are the sanitizer (unknown tags/attrs/styles never enter).
 *   2. (ProseMirror parses the clipboard into a Slice with the schema)
 *   3. transformPasted — node level: the Storydoc normalization policy.
 *   4. (ProseMirror inserts the slice with replaceSelection)
 * The only handlePaste use is the URL-over-selection rule (spec §8).
 */

type PasteKind = "pm" | "storydoc" | "external" | "text";

// Set by the string hooks, consumed by transformPasted in the same paste.
let pasteKind: PasteKind = "external";
// Set when the editor pastes HTML it built itself (a copied element's text),
// so transformPastedHTML takes it as Storydoc content, formatting kept.
let forcedKind: Exclude<PasteKind, "text"> | null = null;

type CopiedEntity = {
  type?: string;
  settings?: { name?: string; content?: unknown };
  childIds?: string[];
};

/**
 * The stored HTML of every text element in a copied element (Cmd+C on a
 * selected element writes it as JSON, copyEntityToClipboard), in layer
 * order. "" when the clipboard holds no such element or no text.
 */
export const copiedElementHtml = (clipboardText: string): string => {
  let payload: {
    entityEntryRootId?: string;
    entities?: Record<string, CopiedEntity>;
  };
  try {
    payload = JSON.parse(clipboardText);
  } catch {
    return "";
  }
  const entities = payload?.entities ?? {};
  const html: string[] = [];
  const visit = (id: string | undefined) => {
    const entity = id ? entities[id] : undefined;
    if (!entity) return;
    const content = entity.settings?.content;
    if (entity.settings?.name === "RichText" && typeof content === "string")
      html.push(content);
    entity.childIds?.forEach(visit);
  };
  visit(payload?.entityEntryRootId);
  return html.join("");
};

const { nodes } = textSchema;

const isList = (type: unknown) =>
  type === nodes.bullet_list || type === nodes.ordered_list;

const mapTop = (fragment: Fragment, fn: (node: PmNode) => PmNode) => {
  const out: PmNode[] = [];
  fragment.forEach((child) => out.push(fn(child)));
  return Fragment.fromArray(out);
};

/**
 * A selection over the whole element (Cmd+A). Pasting over it replaces the
 * element's content outright; ProseMirror's replace kept the first block's
 * context, so in a list everything pasted landed inside the first item.
 */
const selectsWholeDoc = (state: EditorState) => {
  const { from, to, empty } = state.selection;
  return (
    !empty &&
    from <= Selection.atStart(state.doc).from &&
    to >= Selection.atEnd(state.doc).to
  );
};

/**
 * Over the whole element, only lists replace it outright: a pasted list, or a
 * paste over a list element. ProseMirror's replace kept the first block's
 * context, so everything pasted landed inside the first item. Text pasted over
 * a whole heading or paragraph joins it instead and keeps its style and
 * alignment, like typing over a selection (product: works like Google Slides).
 */
const replacesWholeDoc = (state: EditorState, content: Fragment) =>
  selectsWholeDoc(state) &&
  hasBlockContent(content) &&
  (isList(content.firstChild?.type) || isList(state.doc.firstChild?.type));

/**
 * A copy of whole blocks (Cmd+A in another text) is a closed slice. Pasted
 * into a line it replaced or split that line, so a heading lost its style and
 * the text came in with its own alignment (product: works like Google Slides).
 * Text blocks join the line instead, like typing. An empty Normal line still
 * takes the pasted block's style, as any paste does, and lists keep their own
 * rules.
 */
const joinTargetLine = (slice: Slice, state: EditorState): Slice => {
  const { $from, empty } = state.selection;
  const target = $from.parent;
  let textOnly = slice.content.childCount > 0;
  slice.content.forEach((child) => {
    if (!child.isTextblock) textOnly = false;
  });
  const emptyNormalLine =
    empty && target.type === nodes.paragraph && target.content.size === 0;
  if (
    slice.openStart > 0 ||
    !textOnly ||
    !target.isTextblock ||
    emptyNormalLine ||
    replacesWholeDoc(state, slice.content)
  )
    return slice;
  return Slice.maxOpen(slice.content);
};

const isItemsOnly = (fragment: Fragment) => {
  let ok = fragment.childCount > 0;
  fragment.forEach((child) => {
    if (child.type !== nodes.list_item) ok = false;
  });
  return ok;
};

const hasBlockContent = (fragment: Fragment) => {
  let block = false;
  fragment.forEach((child) => {
    if (child.isBlock) block = true;
  });
  return block;
};

// A pasted URL: scheme or www, or a bare domain like `storydoc.com/x`
// (labels, a 2–6 letter TLD, optional port/path) — spec §8 "Paste URL".
const BARE_URL =
  /^(?:(?:https?:\/\/|www\.)\S+|[\w-]+(?:\.[\w-]+)*\.[a-z]{2,6}(?::\d+)?(?:[/?#]\S*)?)$/i;

/**
 * Pasted into a heading, only the first pasted line joins it and takes its
 * style; everything after it keeps its own style, as in Gamma.
 * Separate pasted blocks already did. Lines split by line breaks inside the
 * first pasted block (Storydoc text, Slack, Gmail write them) all joined the
 * heading, so a pasted paragraph turned into one big Title. Split that block
 * at its first break: the rest stays a block of its own type.
 */
const onlyFirstLineJoinsHeading = (slice: Slice, state: EditorState): Slice => {
  const first = slice.content.firstChild;
  if (
    state.selection.$from.parent.type !== nodes.heading ||
    slice.openStart < 1 ||
    !first?.isTextblock
  )
    return slice;
  let breakAt = -1;
  first.forEach((child, offset) => {
    if (breakAt < 0 && child.type === nodes.hard_break) breakAt = offset;
  });
  if (breakAt < 0) return slice;
  const rest = first.content.cut(breakAt + 1);
  const blocks = [first.copy(first.content.cut(0, breakAt))];
  if (rest.size) blocks.push(first.type.create(first.attrs, rest));
  slice.content.forEach((child, _, index) => {
    if (index > 0) blocks.push(child);
  });
  return new Slice(Fragment.fromArray(blocks), slice.openStart, slice.openEnd);
};

/**
 * A clipboard of files only (an image from Finder): the editor shell turns it
 * into an Image element. ProseMirror must not touch it — with nothing to paste
 * it falls back to "capture paste", focusing an off-screen box near the top of
 * the window, which scrolled the story to the top and back (QA E15).
 */
export const isFileOnlyPaste = (event: ClipboardEvent): boolean => {
  const data = event.clipboardData;
  if (!data || data.getData("text/plain") || data.getData("text/html")) {
    return false;
  }
  return Array.from(data.items).some((item) => item.kind === "file");
};

/** The Storydoc paste policy on parsed clipboard content (spec §2). */
const normalizePastedContent = (
  fragment: Fragment,
  kind: Exclude<PasteKind, "pm">,
): Fragment => {
  let content = stripTrailingBreaks(fragment);
  if (kind === "external") content = mergeAdjacentLists(content);
  if (kind !== "text") {
    content = dropEmptyParagraphs(content);
    content = normalizePastedTypography(content, {
      external: kind === "external",
    });
    // Plain text keeps the inherited (centre) alignment — spec §2.3.
    content = applyDefaultAlignment(content, "left");
  }
  return content;
};

/**
 * Chrome on Windows hands clipboard HTML back wrapped as
 * `<html>\r\n<body>\r\n<!--StartFragment-->…<!--EndFragment-->\r\n</body>\r\n</html>`.
 * ProseMirror keeps whitespace when it restores its own slice, so those line
 * breaks became empty lines and the copied text landed as a separate block in
 * its source style instead of joining the line (Windows only).
 */
const clipboardFragment = (html: string): string =>
  html.match(/<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/)?.[1] ?? html;

const kindOf = (html: string): Exclude<PasteKind, "text"> => {
  if (html.includes("data-pm-slice")) return "pm";
  if (isInternalStorydocPaste(html)) return "storydoc";
  return "external";
};

/**
 * Clipboard HTML as the content of a new text element — a paste onto the
 * canvas with no text open. Same policy as a paste into the editor, so
 * formatting copied from Storydoc survives (QA E11) and a Notion copy arrives
 * as text, not as its markdown text/plain (QA E4).
 */
export const clipboardHtmlToContent = (html: string): string => {
  const kind = kindOf(html);
  const source =
    kind === "storydoc"
      ? extractInternalContent(html)
      : kind === "external"
        ? normalizeExternalHtml(html)
        : html;
  const dom = new window.DOMParser().parseFromString(source, "text/html");
  const parsed = parser.parse(dom.body).content;
  const content = capListDepth(
    textSchema.topNodeType.create(
      null,
      kind === "pm" ? parsed : normalizePastedContent(parsed, kind),
    ),
  );
  return serializeHtml(content);
};

export const pasteProps: Pick<
  EditorProps,
  | "transformPastedText"
  | "transformPastedHTML"
  | "transformPasted"
  | "transformCopied"
  | "clipboardTextSerializer"
  | "handlePaste"
> = {
  /**
   * The plain-text copy: one line per text block, list items indented two
   * spaces per level below the first, which is the indent a plain-text paste
   * reads back as nesting. ProseMirror's default wrote every block flush left
   * with a blank line between, so a nested list copied out and pasted as
   * plain text (Cmd+Shift+V, a notes app) came back flat (product QA E7).
   * Blocks keep the blank line between them; items of one list don't.
   */
  clipboardTextSerializer(slice) {
    const lines: Array<{ text: string; inList: boolean }> = [];
    const leafText = (leaf: PmNode) =>
      leaf.type === nodes.hard_break
        ? "\n"
        : (leaf.type.spec.leafText?.(leaf) ?? "");
    const walk = (fragment: Fragment, depth: number) =>
      fragment.forEach((node) => {
        if (node.isTextblock) {
          lines.push({
            text:
              "  ".repeat(Math.max(depth - 1, 0)) +
              node.textBetween(0, node.content.size, "", leafText),
            inList: depth > 0,
          });
          return;
        }
        walk(node.content, isList(node.type) ? depth + 1 : depth);
      });
    walk(slice.content, 0);
    return lines
      .map(({ text, inList }, i) =>
        i === 0
          ? text
          : `${inList && lines[i - 1].inList ? "\n" : "\n\n"}${text}`,
      )
      .join("");
  },

  /**
   * Copying part of a styled block — a counter on its own, a few words of a
   * Title — keeps how it looked: the block's style rides along as an inline
   * segment, so pasted into normal text it stays Title-sized (QA G2). Whole
   * blocks carry their own type and are left as they are.
   */
  transformCopied(slice, view) {
    // Only a copy that stays inside one block (text, or a clicked atom).
    const { $from, $to } = view.state.selection;
    if (!$from.sameParent($to) || !$from.parent.isTextblock) return slice;
    const item =
      $from.depth > 1 && $from.node(-1).type === nodes.list_item
        ? $from.node(-1)
        : null;
    const style = blockImpliedStyle($from.parent, item);
    if (style === "normal") return slice;
    const segment = textSchema.marks.text_style.create({ style });
    const addStyle = (child: PmNode): PmNode =>
      child.isInline
        ? textSchema.marks.text_style.isInSet(child.marks)
          ? child
          : child.mark(segment.addToSet(child.marks))
        : child.copy(mapTop(child.content, addStyle));
    return new Slice(
      mapTop(slice.content, addStyle),
      slice.openStart,
      slice.openEnd,
    );
  },

  transformPastedText(text) {
    pasteKind = "text";
    return text;
  },

  transformPastedHTML(html) {
    pasteKind = forcedKind ?? kindOf(html);
    forcedKind = null;
    // Our own clipboard format — ProseMirror restores the exact slice.
    if (pasteKind === "pm") return clipboardFragment(html);
    // Copy from another Storydoc text surface: keep formatting, drop the
    // element/layout wrappers so containers don't nest.
    if (pasteKind === "storydoc") return extractInternalContent(html);
    return normalizeExternalHtml(html);
  },

  transformPasted(slice, view: EditorView) {
    const kind = pasteKind;
    pasteKind = "external";
    const { state } = view;
    if (kind === "pm")
      return onlyFirstLineJoinsHeading(joinTargetLine(slice, state), state);

    let content = normalizePastedContent(slice.content, kind);

    // Over the whole element a list replaces everything (handlePaste), so it
    // keeps its own structure: no conversion into the old list's items.
    if (replacesWholeDoc(state, content)) {
      return new Slice(
        mapTop(content, (child) =>
          isList(child.type) ? capListDepth(child, MAX_LIST_DEPTH) : child,
        ),
        0,
        0,
      );
    }

    const list = enclosingList(state);
    const currentDepth = listDepthAt(state.doc, state.selection.from);

    // Spec §2.4 — block content pasted into a list becomes list items.
    if (list && hasBlockContent(content)) {
      const $from = state.selection.$from;
      const item =
        $from.node(-1).type === nodes.list_item ? $from.node(-1) : null;
      const merged = blocksToListItems(
        content,
        list.node.type as typeof nodes.bullet_list,
        list.node.attrs,
        {
          textStyle: item?.attrs.textStyle ?? null,
          align: item?.attrs.align ?? null,
          icon: item?.attrs.icon ?? null,
          iconName: item?.attrs.iconName ?? null,
        },
        kind === "text",
      );
      // The pasted items join the current list at the caret item's level, so
      // they may use that level and the ones left below it (one more than
      // MAX - depth: a fourth level pasted into a top-level list was capped
      // to the third, product QA E7).
      const capped = capListDepth(
        merged,
        Math.max(MAX_LIST_DEPTH - currentDepth + 1, 1),
      );
      // A closed slice of bare items: handlePaste below inserts them after
      // the current item, so every pasted line becomes its own item (spec
      // §2.4) instead of the first line merging into the caret's item.
      return new Slice(capped.content, 0, 0);
    }

    // Plain text has no layout of its own: its lines take the alignment,
    // spacing and direction of the line they land in, as Enter does. They
    // came in on the layout's alignment, so text pasted into a line centred
    // with the toolbar lost the centring (product QA E6).
    if (kind === "text") {
      const target = state.selection.$from.parent;
      content = mapTop(content, (child) =>
        child.type === nodes.paragraph
          ? nodes.paragraph.create(
              {
                ...child.attrs,
                align: child.attrs.align ?? target.attrs.align ?? null,
                lineHeight:
                  child.attrs.lineHeight ?? target.attrs.lineHeight ?? null,
                direction:
                  child.attrs.direction ?? target.attrs.direction ?? null,
              },
              child.content,
              child.marks,
            )
          : child,
      );
    }

    // Spec §4.2 — depth ≤ MAX_LIST_DEPTH relative to where the content lands.
    content = mapTop(content, (child) =>
      isList(child.type)
        ? capListDepth(child, Math.max(MAX_LIST_DEPTH - currentDepth, 1))
        : child,
    );
    // Into an empty Normal line the first pasted block replaces it (keeping
    // the block's own tag/alignment); into text it merges — ProseMirror
    // default. An empty heading is the style the element starts in (its
    // placeholder line, e.g. a slide's Title), so pasted text joins it and
    // takes that style, like typing.
    const { $from, empty } = state.selection;
    const emptyTarget =
      empty &&
      $from.parent.type === nodes.paragraph &&
      $from.parent.content.size === 0;
    // A list pasted into a paragraph stays one list after it. Opened at the
    // start, its first item merged into the paragraph and its sub-items and
    // later items split off into separate lists.
    const startsWithList =
      !!content.firstChild && isList(content.firstChild.type);
    // Flattening deep lists and dropping edge blocks can leave the content
    // shallower than the clipboard slice was open; an openEnd deeper than the
    // content crashes replaceSelection (QA E9), so never exceed what it allows.
    const fits = Slice.maxOpen(content);
    return onlyFirstLineJoinsHeading(
      new Slice(
        content,
        (emptyTarget && hasBlockContent(content)) || startsWithList
          ? 0
          : Math.min(slice.openStart, fits.openStart),
        Math.min(slice.openEnd, fits.openEnd),
      ),
      state,
    );
  },

  handlePaste(view, event, slice) {
    if (!event.clipboardData) return false;

    // A copied Storydoc *element* (JSON) pastes its text, formatting kept,
    // through this same pipeline, so list and select-all rules still apply.
    // It used to be ignored, as Froala does (product QA E12).
    if (isEntityPaste(event)) {
      const html = copiedElementHtml(event.clipboardData.getData("text"));
      if (html) {
        forcedKind = "storydoc";
        view.pasteHTML(html, {
          clipboardData: {
            getData: (type: string) => (type === "text/html" ? html : ""),
          },
        } as unknown as ClipboardEvent);
      }
      return true;
    }

    // Slack's message box copies no HTML, only its own rich text: build the
    // HTML from that so the formatting comes along (product QA E4). A plain
    // paste (Cmd+Shift+V) stays plain.
    const plainPaste = (view as unknown as { input?: { shiftKey?: boolean } })
      .input?.shiftKey;
    const slackHtml = plainPaste
      ? ""
      : slackTextyToHtml(event.clipboardData.getData("slack/texty"));
    if (slackHtml && !event.clipboardData.getData("text/html")) {
      view.pasteHTML(slackHtml, {
        clipboardData: {
          getData: (type: string) => (type === "text/html" ? slackHtml : ""),
        },
      } as unknown as ClipboardEvent);
      return true;
    }

    // Cmd+A then paste a list, or paste over a list: the pasted blocks become
    // the element's content.
    if (slice.openStart === 0 && replacesWholeDoc(view.state, slice.content)) {
      const { state } = view;
      const tr = state.tr.replaceWith(0, state.doc.content.size, slice.content);
      tr.setSelection(Selection.atEnd(tr.doc));
      view.dispatch(tr.scrollIntoView().setMeta("paste", true));
      return true;
    }

    // Spec §2.4 — items produced by transformPasted for a paste inside a
    // list go right after the caret's item, one item per pasted line. An
    // empty item is replaced instead, so it doesn't stay behind (QA E8).
    if (slice.openStart === 0 && isItemsOnly(slice.content)) {
      const tr = view.state.tr.deleteSelection();
      const $from = tr.selection.$from;
      let depth = $from.depth;
      while (depth > 0 && $from.node(depth).type !== nodes.list_item) depth--;
      if (depth > 0) {
        const item = $from.node(depth);
        const isEmpty = item.childCount === 1 && item.textContent === "";
        const from = isEmpty ? $from.before(depth) : $from.after(depth);
        const to = isEmpty ? $from.after(depth) : from;
        tr.replaceWith(from, to, slice.content);
        tr.setSelection(
          TextSelection.near(tr.doc.resolve(from + slice.content.size), -1),
        );
        view.dispatch(tr.scrollIntoView().setMeta("paste", true));
        return true;
      }
    }

    const raw = event.clipboardData.getData("text/plain") || "";
    const text = raw.trim();
    const html = event.clipboardData.getData("text/html");
    if (!text || html || !BARE_URL.test(text)) return false;

    // Spec §8 — URL over a selection links it; URL at a caret inserts a link.
    const { empty } = view.state.selection;
    if (!empty) return linkSelectionTo(text)(view.state, view.dispatch);
    const mark = textSchema.marks.link.create({
      href: normalizeUrl(text),
      target: "_blank",
    });
    // A space copied around the URL stays, outside the link (QA E10).
    const space = (edge: string) => (edge ? [textSchema.text(" ")] : []);
    const link = Fragment.from([
      ...space(raw.match(/^\s*/)?.[0] ?? ""),
      textSchema.text(text, [mark]),
      ...space(raw.match(/\s*$/)?.[0] ?? ""),
    ]);
    view.dispatch(
      view.state.tr.replaceSelection(new Slice(link, 0, 0)).scrollIntoView(),
    );
    return true;
  },
};
